import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Compass, Handshake } from "lucide-react";
import { useReveal } from "@/lib/animations";
import { ASSETS, CONTACT } from "@/lib/data";

const VALUES = [
  {
    icon: Users,
    title: "Family first",
    text: "Business comes second. Every plan we build starts with the people it protects.",
  },
  {
    icon: ShieldCheck,
    title: "Protection without risk",
    text: "We specialize in strategies with a 0% floor — your money never goes backward in a crash.",
  },
  {
    icon: Compass,
    title: "Education over pressure",
    text: "We explain every option in plain English. You decide. No jargon, no pushy sales.",
  },
  {
    icon: Handshake,
    title: "Independent & unbiased",
    text: "As a brokerage with 50+ A-rated carriers, we work for you — not an insurance company.",
  },
];

export default function About() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <main ref={ref} data-testid="about-page" className="bg-[#F9F6F0]">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
            About Breeze
          </p>
          <h1
            data-reveal
            className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] text-[#0B132B]"
          >
            Insurance that feels like a{" "}
            <span className="text-gradient-breeze">breeze</span>, not a burden.
          </h1>
          <p data-reveal className="mt-7 text-lg text-[#334155] font-light leading-relaxed max-w-xl">
            Breeze Financial Group is an independent life insurance brokerage
            built on a simple belief: every family deserves a plan that grows
            without market risk, pays income they can't outlive, and protects
            the people they love.
          </p>
          <p data-reveal className="mt-5 text-[#334155] font-light leading-relaxed max-w-xl">
            We partner with more than 50 A-rated carriers to design strategies
            around you — not around a product quota. From your first policy to
            generational wealth, we're with you for the long haul.
          </p>
          <div data-reveal className="mt-10 flex flex-wrap gap-x-12 gap-y-6">
            {[
              ["2023", "year founded"],
              ["200+", "licensed life insurance advisors & retirement specialists"],
              ["50+", "A-rated carrier partners"],
            ].map(([v, l]) => (
              <div key={l} data-testid={`about-fact-${v}`}>
                <div className="font-heading font-black text-3xl text-[#0B132B] tracking-tight">
                  {v}
                </div>
                <p className="text-sm text-[#334155] font-light mt-1.5 max-w-[200px] leading-snug">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div data-reveal className="lg:col-span-5">
          <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-[#0B132B]/15">
            <img
              src={ASSETS.familyField}
              alt="Family walking together in a green field"
              className="w-full aspect-[4/5] object-cover"
            />
          </div>
        </div>
      </section>

      <section data-testid="founder-section" className="bg-[#F0EBE0] grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div data-reveal className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#0B132B]/15">
              <img
                src="/brandon-beal.jpg"
                alt="Brandon Beal, Founder & CEO of Breeze Financial Group"
                className="w-full aspect-square object-cover"
                data-testid="founder-photo"
              />
            </div>
            <div className="mt-6">
              <div className="font-heading font-black text-2xl text-[#0B132B] tracking-tight">
                Brandon Beal
              </div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mt-1">
                Founder & CEO
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
              Meet the founder
            </p>
            <h2
              data-reveal
              className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none text-[#0B132B]"
            >
              From the gridiron to{" "}
              <span className="text-gradient-breeze">generational wealth</span>.
            </h2>
            <div data-reveal className="mt-8 space-y-5 text-[#334155] font-light leading-relaxed">
              <p>
                Brandon Beal hails from St. Louis, Missouri. From a young age,
                his parents instilled a tireless work ethic coupled with a
                heightened appreciation of academic values that fueled his
                aspirations of becoming a professional athlete. In high school,
                Brandon excelled in both basketball and football — the latter
                earning him a full athletic scholarship to Northern Illinois
                University. In 2010, he was invited to an NFL workout, though a
                torn ligament in his foot abruptly ended his NFL career journey.
              </p>
              <p>
                It was during rehab that Brandon fully immersed himself in the
                fitness industry, helping others reach their own peak fitness
                levels as a certified personal trainer. Over the next four
                years, the satisfaction of helping his clients overcome a myriad
                of health challenges left Brandon wanting to make an even bigger
                difference in the world.
              </p>
              <p>
                In 2017, Brandon obtained his life insurance credentials — and
                in a span of merely three years, he became one of the top
                producers at his firm while quickly rising up the ranks to
                become one of its most promising leaders. With his proven track
                record of helping others excel in thriving markets, Brandon
                formed his own Insurance Marketing Organization, Breeze
                Financial Group, in 2023 — paving the road for other aspiring
                men and women to mimic his success, and furthering his mission
                of empowering Americans with the financial tools that will
                secure generational wealth for their families.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0B132B] text-white grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28">
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none max-w-2xl"
          >
            What we stand for.
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  data-reveal
                  data-reveal-delay={i * 0.08}
                  data-testid={`value-card-${i}`}
                  className="rounded-3xl bg-white/5 border border-white/10 p-8 transition-colors duration-300 hover:border-[#29ABE2]/50"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#29ABE2]/15 text-[#29ABE2] flex items-center justify-center mb-5">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-xl">{v.title}</h3>
                  <p className="mt-3 text-white/60 font-light leading-relaxed text-sm">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center">
        <h2
          data-reveal
          className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-[#0B132B]"
        >
          Let's talk about your future.
        </h2>
        <p data-reveal className="mt-5 text-[#334155] font-light max-w-md mx-auto">
          Call us at{" "}
          <a href={CONTACT.phoneHref} className="text-[#0a6fc2] font-medium" data-testid="about-phone-link">
            {CONTACT.phone}
          </a>{" "}
          or get a free quote online in minutes.
        </p>
        <div data-reveal className="mt-9">
          <Link
            to="/quote"
            data-testid="about-quote-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
          >
            Get my free quote <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}
